import axios from 'axios';
import { cacheAdapterEnhancer } from 'axios-extensions';
import * as AccountExceptions from '@/constants/account-exceptions';
import { LoginService } from '@/common/services/login.service';
import { UserData } from '@/common/auth/user-data';
import { SCHOOL_APP } from '@/main';
import router from '@/router';

import store from '@/store/store';
import * as Routes from '@/router/routes';
import * as Roles from '@/constants/user-types';

let isRefreshing = false;
let requestsBuffer = [];

/**
 * Use this instance Axios Instance to make Http requests
 * @type {AxiosInstance}
 */
export const HTTP = axios.create({
    baseURL: process.env.API_URL,
    headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        'Expires': '0'

    },
    adapter: cacheAdapterEnhancer(axios.defaults.adapter, { enabledByDefault: false, cacheFlag: 'cache' })
});

const {CancelToken} = axios;

let cancelTokenSource = CancelToken.source();

export function cancelToken(message) {
    cancelTokenSource.cancel(message);
    cancelTokenSource = CancelToken.source();
}

HTTP.interceptors.request.use(request => {
        store.dispatch('showProgress');
        request.headers = {
            ...request.headers,
            'AuthenticationHeader': 'SCHOOL'
        };
        if (!request.headers.global) {
            const { token } = cancelTokenSource;
            request.cancelToken = token;
        }
        const fullUrl = request.baseURL + request.url;
        if (fullUrl.indexOf('/no-auth') === -1 || fullUrl.indexOf('/refresh') !== -1) {
            const userData = UserData.getUserDataFromLocalStorage();
            if (userData) {
                const accessToken = userData.jwtToken;
                request.headers = {
                    ...request.headers,
                    'Authorization': accessToken
                };
            }
        }
        return request;
    },
    error => Promise.reject(error)
);

HTTP.interceptors.response.use(
    response => {
        store.dispatch('hideProgress');
        return response
    },
    async error => {
        store.dispatch('hideProgress');
        if (!axios.isCancel(error)) {
            const userData = UserData.getUserDataFromLocalStorage();
            const { config } = error;
            const { status, data } = error.response;
            if (status === 403) {
                switch (data) {
                    case AccountExceptions.SUSPENDED_SCHOOL:
                        router.push({ name: Routes.PAGE_ACCOUNT_SUSPENDED });
                        break;
                    case AccountExceptions.EMAIL_VERIFICATION_TOKEN_DOES_NOT_EXIST:
                        router.push({ name: Routes.LOGIN });
                        break;
                    case AccountExceptions.INACTIVE_SCHOOL:
                        const isAdmin = userData.userRole === Roles.SCHOOL_ADMIN;
                        if (isAdmin) {
                            router.push({ name: Routes.BILLING_PAGE });
                            store.dispatch('createWarningNotification', 'Your Popsicle account is inactive. Please check your billing info.');
                        }
                        else {
                            router.push({ name: Routes.PERSONAL_INFO_PAGE });
                            store.dispatch('createWarningNotification', 'Your school is inactive. Please contact your administrator');
                        }
                        break;
                    default:
                        router.push({ name: Routes.REGISTRATION_CONFIRM, query: { exception: data } });
                }
            }
            if (status > 403) {
                store.dispatch('createErrorNotification', 'Oops! Something went wrong');
            }
            if (userData) {
                switch ( status) {
                    case 400:
                        if (data && data.showMessageToUser) {
                            store.dispatch('createErrorNotification', error.response.data.message);
                        }
                        throw error;
                    case 401:
                        if (data && typeof data === 'string' && data.includes('mistake in your password.')) throw error;

                        if (!config.url.includes('refresh')) {
                            if (!isRefreshing) {
                                isRefreshing = true;
                                try {
                                    const { expirationToken } = userData;
                                    const { data } = await LoginService.refreshToken(expirationToken);
                                    await store.dispatch('saveUserInfo', data);
                                    onTokenRefreshed(data.jwtToken);
                                } catch (e) {
                                    return navigateToLoginWithError(e);
                                } finally {
                                    isRefreshing = false;
                                }
                                return error;
                            } else {
                                return new Promise(resolve => {
                                    handleExpiration(token => {
                                        const newRequest = {
                                            ...error.config,
                                            baseURL: ''
                                        };
                                        newRequest.headers = {
                                            ...newRequest.headers,
                                            Authorization: token,
                                        };
                                        resolve(axios(newRequest));
                                    });

                                })
                            }
                        } else {
                            return navigateToLoginWithError(error);
                        }

                }
            }
            throw error;
        }
    }
);

function handleExpiration(callback) {
    requestsBuffer.push(callback);
}

function onTokenRefreshed(token) {
    requestsBuffer.forEach(request => request(token));
}

function navigateToLoginWithError(error) {
    UserData.removeUserDataFromLocalStorage();
    SCHOOL_APP.$router.push({name: Routes.LOGIN});
    return Promise.reject(error);
}


// WEBPACK FOOTER //
// ./src/common/utils/http.js